import { z } from 'zod';

/**
 * Zod schemas for user settings validation
 * Used to validate localStorage settings data
 */

// AutoPaySettings schema
export const autoPaySettingsSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  enabled: z.boolean(),
  paymentMethodId: z.string(),
  amount: z.number().nonnegative().nullable(), // null = full rent amount
  dayOfMonth: z.number().int().min(1).max(31), // Day of month to charge (1-31)
  startDate: z.string(),
  endDate: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
  stripePriceId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AutoPaySettings = z.infer<typeof autoPaySettingsSchema>;

// PaymentMethod schema
export const paymentMethodSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  type: z.enum(['credit_card', 'debit_card', 'ach', 'bank_account'] as const),
  name: z.string(),
  last4: z.string().optional(),
  expiryMonth: z.number().optional(),
  expiryYear: z.number().optional(),
  bankName: z.string().optional(),
  accountType: z.enum(['checking', 'savings'] as const).nullish(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  stripePaymentMethodId: z.string().optional(),
  stripeCustomerId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

// Array of payment methods schema
export const paymentMethodsSchema = z.array(paymentMethodSchema);
export type PaymentMethods = z.infer<typeof paymentMethodsSchema>;

// Theme schema
export const themeSchema = z.enum(['light', 'dark'] as const);
export type Theme = z.infer<typeof themeSchema>;

// OwnerSettings schema
export const ownerSettingsSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  notifications: z.object({
    statementDelivery: z.enum(['email', 'portal', 'both'] as const),
    financialAlerts: z.object({
      enabled: z.boolean(),
      threshold: z.number(),
    }),
    propertyUpdates: z.object({
      enabled: z.boolean(),
      types: z.array(z.enum(['maintenance', 'tenant', 'financial', 'compliance'] as const)),
    }),
    communicationPreferences: z.object({
      email: z.boolean(),
      sms: z.boolean(),
      portal: z.boolean(),
    }),
  }),
  financial: z.object({
    preferredPaymentMethod: z.enum(['ach', 'wire', 'check'] as const),
    distributionPreferences: z.object({
      frequency: z.enum(['monthly', 'quarterly', 'annually'] as const),
      minimumAmount: z.number(),
      autoDistribute: z.boolean(),
    }),
    taxDocumentPreferences: z.object({
      deliveryMethod: z.enum(['email', 'portal', 'mail'] as const),
      format: z.enum(['pdf', 'excel'] as const),
    }),
    reportingPreferences: z.object({
      frequency: z.enum(['monthly', 'quarterly'] as const),
      format: z.enum(['pdf', 'excel', 'both'] as const),
      includeCharts: z.boolean(),
    }),
  }),
  property: z.object({
    defaultView: z.enum(['list', 'grid', 'map'] as const),
    grouping: z.enum(['none', 'type', 'location', 'performance'] as const),
    filtering: z.object({
      showVacant: z.boolean(),
      showOccupied: z.boolean(),
      minOccupancy: z.number(),
    }),
    dashboardCustomization: z.object({
      showCharts: z.boolean(),
      showMetrics: z.boolean(),
      showAlerts: z.boolean(),
    }),
  }),
  communication: z.object({
    preferredMethod: z.enum(['email', 'sms', 'portal'] as const),
    contactInformation: z.object({
      primaryEmail: z.string().email(),
      secondaryEmail: z.string().email().optional(),
      primaryPhone: z.string(),
      secondaryPhone: z.string().optional(),
    }),
    emergencyContact: z.object({
      name: z.string(),
      relationship: z.string(),
      phone: z.string(),
      email: z.string().email().optional(),
    }),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type OwnerSettings = z.infer<typeof ownerSettingsSchema>;

// TenantSettings schema
export const tenantSettingsSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  notifications: z.object({
    paymentReminders: z.object({
      enabled: z.boolean(),
      daysBefore: z.array(z.number().int()),
      method: z.enum(['email', 'sms', 'both'] as const),
    }),
    maintenanceUpdates: z.object({
      enabled: z.boolean(),
      method: z.enum(['email', 'sms', 'portal', 'all'] as const),
    }),
    leaseRenewalReminders: z.object({
      enabled: z.boolean(),
      daysBefore: z.number().int(),
      method: z.enum(['email', 'sms', 'both'] as const),
    }),
    communicationPreferences: z.object({
      email: z.boolean(),
      sms: z.boolean(),
      portal: z.boolean(),
    }),
  }),
  payment: z.object({
    autoPay: z.object({
      enabled: z.boolean(),
      paymentMethodId: z.string().optional(),
      amount: z.number().nullable(),
      dayOfMonth: z.number().int().min(1).max(31),
    }),
    defaultPaymentMethodId: z.string().optional(),
    receiptDelivery: z.object({
      method: z.enum(['email', 'portal', 'both'] as const),
      autoEmail: z.boolean(),
    }),
    reminderSettings: z.object({
      enabled: z.boolean(),
      daysBefore: z.array(z.number().int()),
    }),
  }),
  privacy: z.object({
    twoFactorEnabled: z.boolean(),
    dataSharing: z.object({
      allowMarketing: z.boolean(),
      allowAnalytics: z.boolean(),
      shareWithPM: z.boolean(),
    }),
  }),
  documents: z.object({
    deliveryMethod: z.enum(['email', 'portal', 'both'] as const),
    leaseRenewal: z.object({
      autoRenew: z.boolean(),
      reminderDays: z.number().int(),
    }),
    storagePreferences: z.object({
      keepAllDocuments: z.boolean(),
      autoArchiveAfter: z.number().int(),
    }),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

// IntegrationSettings schema
export const integrationSettingsSchema = z.object({
  id: z.string(),
  integrationType: z.string(), // IntegrationType enum
  status: z.string(), // IntegrationStatus enum
  name: z.string(),
  description: z.string(),
  config: z.object({
    apiKey: z.string().optional(),
    secretKey: z.string().optional(),
    publishableKey: z.string().optional(),
    accountId: z.string().optional(),
    userId: z.string().optional(),
    region: z.string().optional(),
    bucketName: z.string().optional(),
    webhookUrl: z.string().optional(),
    webhookSecret: z.string().optional(),
  }),
  lastHealthCheck: z.string().optional(),
  lastError: z.string().optional(),
  errorCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type IntegrationSettings = z.infer<typeof integrationSettingsSchema>;

// VendorSettings schema
export const vendorSettingsSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  notifications: z.object({
    jobAssignments: z.object({
      enabled: z.boolean(),
      method: z.enum(['email', 'sms', 'both'] as const),
    }),
    paymentNotifications: z.object({
      enabled: z.boolean(),
    }),
    messages: z.object({
      enabled: z.boolean(),
    }),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type VendorSettings = z.infer<typeof vendorSettingsSchema>;

// InvestorSettings schema
export const investorSettingsSchema = z.object({
  id: z.string(),
  investorId: z.string(),
  notifications: z.object({
    distributionAlerts: z.object({
      enabled: z.boolean(),
      method: z.enum(['email', 'portal', 'both'] as const),
    }),
    reportDelivery: z.object({
      enabled: z.boolean(),
      frequency: z.enum(['monthly', 'quarterly'] as const),
    }),
    complianceUpdates: z.object({
      enabled: z.boolean(),
    }),
  }),
  reporting: z.object({
    format: z.enum(['pdf', 'excel', 'both'] as const),
    includeCharts: z.boolean(),
  }),
  communication: z.object({
    preferredMethod: z.enum(['email', 'portal'] as const),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type InvestorSettings = z.infer<typeof investorSettingsSchema>;

// MarketingSiteSettings schema
export const marketingSiteSettingsSchema = z.object({
  id: z.string(),
  siteName: z.string(),
  heroHeadline: z.string(),
  heroDescription: z.string(),
  heroImageUrl: z.string(),
  contactPhone: z.string(),
  contactEmail: z.string().email(),
  contactAddress: z.string(),
  footerDescription: z.string(),
  socialMedia: z.object({
    facebook: z.string().optional(),
    instagram: z.string().optional(),
    twitter: z.string().optional(),
  }),
  seoTitle: z.string(),
  seoDescription: z.string(),
  customDomain: z.string().optional(),
  primaryBrandColor: z.string(),
});

export type MarketingSiteSettings = z.infer<typeof marketingSiteSettingsSchema>;
